import { useState, useEffect } from 'react';
import styled from 'styled-components';

const MIN_DESCRIPTION_LENGTH = 120;

function NewPropertyPreview({ photos, onBack, onNext, initialData = {} }) {

  const validPhotos = photos || [];

  const [activeIndex, setActiveIndex] = useState(0);
  const [description, setDescription] = useState('');
  const [showDescriptionAlert, setShowDescriptionAlert] = useState(false);

  /* ===============================
     OBTENER SRC DE IMAGEN
     (soporta blob preview o url)
     =============================== */

  const getImageSrc = (photo) =>
    typeof photo === "string" ? photo : photo?.preview || "";

  /* ===============================
     PRECARGAR DESCRIPCIÓN (EDIT)
     =============================== */

  useEffect(() => {

    if (!initialData?.description) return;

    setDescription(initialData.description);

  }, [initialData]);

  const next = () => {
    if (activeIndex < validPhotos.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const prev = () => {
    if (activeIndex > 0) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const isDescriptionValid =
    description.trim().length >= MIN_DESCRIPTION_LENGTH;

  const descriptionLength = description.trim().length;
  const remainingCharacters = Math.max(
    MIN_DESCRIPTION_LENGTH - descriptionLength,
    0
  );

  const handleContinue = () => {
    if (!isDescriptionValid) {
      setShowDescriptionAlert(true);
      return;
    }

    onNext({ description: description.trim() });
  };

  return (
    <Page>

      <Container>

        <Header>
          <Title>Describe tu Propiedad</Title>
          <Subtitle>
            Ayuda a los interesados a entender mejor tu propiedad
          </Subtitle>
        </Header>

        {validPhotos.length > 0 && (

          <Gallery>

            <Main>

              <img src={getImageSrc(validPhotos[activeIndex])} alt="" />

              <Nav>

                <button
                  onClick={prev}
                  disabled={activeIndex === 0}
                >
                  ‹
                </button>

                <button
                  onClick={next}
                  disabled={activeIndex === validPhotos.length - 1}
                >
                  ›
                </button>

              </Nav>

            </Main>

            <Thumbs>

              {validPhotos.map((p, i) => (

                <Thumb
                  key={i}
                  active={i === activeIndex}
                  onClick={() => setActiveIndex(i)}
                >
                  <img src={getImageSrc(p)} alt="" />
                </Thumb>

              ))}

            </Thumbs>

          </Gallery>

        )}

        <Block>

          <Label>Descripción</Label>

          <HelperText>
            Contá cómo son los ambientes, la ubicación y aquello que hace
            especial a la propiedad.
          </HelperText>

          <Textarea
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (e.target.value.trim().length >= MIN_DESCRIPTION_LENGTH) {
                setShowDescriptionAlert(false);
              }
            }}
            onBlur={() => setShowDescriptionAlert(true)}
            aria-describedby="description-requirement description-count"
            aria-invalid={showDescriptionAlert && !isDescriptionValid}
            placeholder="Ej: Casa luminosa, con ambientes amplios y excelente distribución…"
          />

          {!isDescriptionValid && (
            <ValidationAlert
              id="description-requirement"
              role={showDescriptionAlert ? 'alert' : 'status'}
              $error={showDescriptionAlert}
            >
              <strong>La descripción debe tener al menos 120 caracteres.</strong>
              <span>
                Agregá información sobre los ambientes, el entorno y aquello que hace
                especial a la propiedad. Te faltan {remainingCharacters} caracteres.
              </span>
            </ValidationAlert>
          )}

          <MetaRow>

            <CharCount id="description-count" $valid={isDescriptionValid}>
              {descriptionLength} / {MIN_DESCRIPTION_LENGTH} caracteres mínimos
            </CharCount>

          </MetaRow>

        </Block>

        <Actions>

          <Secondary type="button" onClick={onBack}>
            Atrás
          </Secondary>

          <Primary
            type="button"
            $disabled={!isDescriptionValid}
            aria-disabled={!isDescriptionValid}
            onClick={handleContinue}
          >
            Continuar
          </Primary>

        </Actions>

      </Container>

    </Page>
  );
}

export default NewPropertyPreview;

/* ================= STYLES ================= */

const Page = styled.div`
  padding-top: 38px;
  background: #f3f0ea;
  min-height: 100vh;
  min-width:0;
  overflow-x:hidden;
  @media(max-width:600px){padding-top:28px;}
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 28px 160px;
  @media(max-width:900px){padding:0 16px 150px;}
`;

const Header = styled.div`
  margin-bottom: 44px;
  @media(max-width:600px){margin-bottom:28px;}
`;

const Title = styled.h1`
  font-size: 38px;
  font-weight: 600;
  overflow-wrap:anywhere;
  @media(max-width:600px){font-size:32px;line-height:1.05;}
`;

const Subtitle = styled.p`
  font-size: 18px;
  line-height: 1.55;
  color: #666;
`;

const Gallery = styled.div`
  margin-bottom: 60px;
  min-width:0;
  @media(max-width:600px){margin-bottom:32px;}
`;

const Main = styled.div`
  position: relative;

  img {
    width: 100%;
    height: 520px;
    object-fit: cover;
    border-radius: 28px;
    display:block;
    @media(max-width:600px){height:auto;aspect-ratio:4/3;border-radius:20px;}
  }
`;

const Nav = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  button {
    background: rgba(0,0,0,0.4);
    color: white;
    border: none;
    font-size: 42px;
    padding: 10px 22px;
    cursor: pointer;

    &:disabled {
      opacity: 0.2;
      cursor: default;
    }
  }
`;

const Thumbs = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 18px;
  overflow-x: auto;
  padding-bottom:5px;
`;

const Thumb = styled.div`
  border: ${({ active }) =>
    active ? '3px solid #C66F4E' : '3px solid transparent'};
  border-radius: 16px;
  cursor: pointer;

  img {
    width: 120px;
    height: 80px;
    object-fit: cover;
    border-radius: 14px;
  }
`;

const Block = styled.div`
  background: white;
  padding: 40px;
  border-radius: 32px;
  box-shadow: 0 40px 90px rgba(0,0,0,0.08);
  margin-bottom: 40px;
  min-width:0;
  @media(max-width:600px){padding:24px 18px;border-radius:22px;}
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 18px;
  margin-bottom: 8px;
  display: block;
`;

const HelperText = styled.p`
  font-size: 16px;
  line-height: 1.55;
  color: #5f6f72;
  margin-bottom: 14px;
`;

const Textarea = styled.textarea`
  width: 100%;
  height: 150px;
  border-radius: 18px;
  border: 1px solid #ddd;
  padding: 18px;
  font-size: 17px;
  resize: none;
  box-sizing: border-box; /* 🔑 FIX */
  line-height: 1.55;

  &:focus {
    outline: none;
    border-color: #C66F4E;
    box-shadow: 0 0 0 3px rgba(198,111,78,0.15);
  }
`;

const MetaRow = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap:12px;
  @media(max-width:560px){align-items:flex-start;flex-direction:column;}
`;

const CharCount = styled.span`
  font-size: 15px;
  font-weight: 600;
  color: ${({ $valid }) => ($valid ? '#287a54' : '#68777b')};
`;

const ValidationAlert = styled.div`
  display: grid;
  gap: 4px;
  margin-top: 14px;
  padding: 15px 17px;
  border: 1px solid ${({ $error }) => ($error ? 'rgba(181, 64, 47, .32)' : 'rgba(198, 111, 78, .22)')};
  border-radius: 14px;
  background: ${({ $error }) => ($error ? '#fff2ef' : '#fff8f3')};
  color: ${({ $error }) => ($error ? '#92392d' : '#6c4a3c')};
  font-size: 15px;
  line-height: 1.5;

  strong { font-size: 16px; }
`;

const Actions = styled.div`
  display: flex;
  justify-content: space-between;
  gap:14px;
  @media(max-width:900px){position:fixed;z-index:1100;right:0;bottom:0;left:0;display:grid;grid-template-columns:1fr 1.45fr;gap:10px;padding:13px 16px calc(13px + env(safe-area-inset-bottom));border-top:1px solid rgba(41,71,79,.1);background:rgba(255,253,249,.96);backdrop-filter:blur(14px);button{width:100%;min-height:52px;padding:0;border-radius:15px;font:inherit;font-size:.92rem;font-weight:850;}button:first-child{border:1px solid #d5dfdc;background:#fff;color:#51696e;box-shadow:none;}button:last-child{border:0;background:#c66f4e;color:#fff;box-shadow:0 9px 20px rgba(198,111,78,.2);}}
`;

const Primary = styled.button`
  background: linear-gradient(135deg, #C66F4E 0%, #ff9d5a 100%);
  color: white;
  padding: 16px 44px;
  border-radius: 999px;
  border: none;
  font-weight: 600;
  font-size: 17px;
  letter-spacing: 0.3px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 12px 28px rgba(198,111,78, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 38px rgba(198,111,78, 0.45);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 10px 20px rgba(198,111,78, 0.35);
  }

  ${({ $disabled }) => $disabled && `
    opacity: .72;
  `}
`;

const Secondary = styled.button`
  background: none;
  border: none;
  font-size: 16px;
  color: #666;
  cursor: pointer;
`;
